import { useMemo } from 'react';
import { getSiteColorDark, availabilityColors, availabilityColorsDark } from '../../planning/colors';
import { getMachineAvailability, AVAILABILITY, ENGINE_TYPES, STATUSES } from '../../planning/constants';
import { computeRentalExtensionSuggestions } from '../../planning/utils/rentalExtensionSuggestions';
import { computeOverdueReturnBookings } from '../../planning/utils/overdueReturnBookings';
import type { AvailabilityKey, EngineTypeKey, Machine, OverdueReturnBooking, StatusKey } from '../../planning/types';
import type {
  CategoryDatum,
  ExtensionSettings,
  PieChartDatum,
  SiteOccupancyDatum,
  SubcontractorDatum,
  TimelineDatum,
} from '../types';
import { DASHBOARD_LABELS as LABELS } from '../../../utils/labels';
import { appNow } from '../../../utils/timezone';

// Bars beyond this rank are folded into a single "Overige" bucket instead of
// being silently dropped — category is free text and can have far more than
// a handful of distinct values (unlike sites, which are a managed, bounded list).
// Clicking "Overige" expands the chart to show every category (see categoriesExpanded).
const CATEGORY_TOP_N = 9;
export const CATEGORY_OTHER_ID = '__other__';

export function useDashboardChartData(
  machines: Machine[],
  isDark: boolean,
  today: string,
  categoriesExpanded: boolean,
  extensionSettings: ExtensionSettings,
  pickupHandinEnabled: boolean
) {
  const allBookings = useMemo(() => machines.flatMap((m) => m.bookings ?? []), [machines]);

  const extensionSuggestions = useMemo(
    () =>
      computeRentalExtensionSuggestions(
        machines,
        today,
        extensionSettings.windowDays,
        extensionSettings.considerOptions
      ),
    [machines, today, extensionSettings]
  );

  const overdueReturnBookings = useMemo<OverdueReturnBooking[]>(
    () => (pickupHandinEnabled ? computeOverdueReturnBookings(machines) : []),
    [machines, pickupHandinEnabled]
  );

  const statusChartData = useMemo<PieChartDatum[]>(() => {
    const counts: Record<StatusKey, number> = { to_order: 0, ordered: 0, collected: 0 };
    machines.forEach((m) => {
      if (m.status in counts) counts[m.status as StatusKey]++;
    });
    const colors = isDark
      ? { to_order: '#a8a29e', ordered: '#60a5fa', collected: '#34d399' }
      : { to_order: '#a8a29e', ordered: '#3b82f6', collected: '#10b981' };
    return (Object.keys(STATUSES) as StatusKey[])
      .map((key) => ({ key, name: STATUSES[key].label, value: counts[key], color: colors[key] }))
      .filter((d) => d.value > 0);
  }, [machines, isDark]);

  const availabilityChartData = useMemo<PieChartDatum[]>(() => {
    const counts: Record<AvailabilityKey, number> = { available: 0, in_use: 0, inactive: 0, reserved: 0 };
    machines.forEach((m) => {
      counts[getMachineAvailability(m, pickupHandinEnabled)]++;
    });
    const colors = isDark ? availabilityColorsDark : availabilityColors;
    return (['available', 'in_use', 'reserved', 'inactive'] as AvailabilityKey[])
      .map((key) => ({
        key,
        name: AVAILABILITY[key].label,
        value: counts[key],
        color: colors[key],
      }))
      .filter((d) => d.value > 0);
  }, [machines, isDark, pickupHandinEnabled]);

  const engineTypeChartData = useMemo<PieChartDatum[]>(() => {
    const counts: Record<EngineTypeKey, number> = { none: 0, electric: 0, diesel: 0, hybrid: 0 };
    machines.forEach((m) => {
      if (m.engine_type in counts) counts[m.engine_type]++;
    });
    // Fixed categorical order (none/electric/diesel/hybrid) — never cycled.
    const colors = isDark
      ? { none: '#0d9488', electric: '#0891b2', diesel: '#8b5cf6', hybrid: '#65a30d' }
      : { none: '#0d9488', electric: '#0891b2', diesel: '#7c3aed', hybrid: '#4d7c0f' };
    return (Object.keys(ENGINE_TYPES) as EngineTypeKey[])
      .map((key) => ({ key, name: ENGINE_TYPES[key].label, value: counts[key], color: colors[key] }))
      .filter((d) => d.value > 0);
  }, [machines, isDark]);

  const allCategoryData = useMemo<CategoryDatum[]>(() => {
    const counts: Record<string, number> = {};
    machines.forEach((m) => {
      if (!m.category) return;
      counts[m.category] = (counts[m.category] ?? 0) + 1;
    });
    // Magnitude comparison, not identity — a single flat hue (the app's primary color).
    const color = isDark ? '#a8b4ff' : '#2243d0';
    return Object.entries(counts)
      .map(([category, count]) => ({ id: category, name: category, machines: count, color }))
      .sort((a, b) => b.machines - a.machines || a.name.localeCompare(b.name));
  }, [machines, isDark]);

  const categoryChartData = useMemo<CategoryDatum[]>(() => {
    if (categoriesExpanded || allCategoryData.length <= CATEGORY_TOP_N) return allCategoryData;
    const top = allCategoryData.slice(0, CATEGORY_TOP_N);
    const rest = allCategoryData.slice(CATEGORY_TOP_N);
    const otherCount = rest.reduce((sum, d) => sum + d.machines, 0);
    // Muted/neutral tone (reused from bookingGroup "past") — reads as an aggregate, not a real category.
    const otherColor = isDark ? '#a8a29e' : '#78716c';
    return [
      ...top,
      { id: CATEGORY_OTHER_ID, name: LABELS.categoryOther(rest.length), machines: otherCount, color: otherColor },
    ];
  }, [allCategoryData, categoriesExpanded, isDark]);

  const siteOccupancyData = useMemo<SiteOccupancyDatum[]>(() => {
    const siteMap: Record<string, { name: string; machineIds: Set<string>; color: string }> = {};
    allBookings.forEach((b) => {
      if (!b.sites) return;
      if (!siteMap[b.site_id]) {
        siteMap[b.site_id] = {
          name: b.sites.name,
          machineIds: new Set(),
          color: isDark ? getSiteColorDark(b.sites.color) : b.sites.color,
        };
      }
      siteMap[b.site_id].machineIds.add(b.machine_id);
    });
    return Object.entries(siteMap)
      .map(([id, { name, machineIds, color }]) => ({ id, name, machines: machineIds.size, color }))
      .sort((a, b) => b.machines - a.machines)
      .slice(0, 12);
  }, [allBookings, isDark]);

  const subcontractorData = useMemo<SubcontractorDatum[]>(() => {
    const subMap: Record<string, { name: string; boekingen: number }> = {};
    allBookings.forEach((b) => {
      if (!b.subcontractor) return;
      if (!subMap[b.subcontractor_id]) {
        subMap[b.subcontractor_id] = { name: b.subcontractor.name, boekingen: 0 };
      }
      subMap[b.subcontractor_id].boekingen += 1;
    });
    return Object.values(subMap).sort((a, b) => b.boekingen - a.boekingen).slice(0, 12);
  }, [allBookings]);

  const timelineData = useMemo<TimelineDatum[]>(() => {
    return Array.from({ length: 14 }, (_, i) => {
      const date = appNow().add(i, 'day');
      const dateStr = date.format('YYYY-MM-DD');
      const arrivals = machines.filter((m) => m.start_date === dateStr).length;
      const returns = machines.filter((m) => m.end_date === dateStr).length;
      return {
        date: date.format('D MMM'),
        aankomsten: arrivals,
        retours: returns,
      };
    });
  }, [machines]);

  return {
    extensionSuggestions,
    overdueReturnBookings,
    statusChartData,
    availabilityChartData,
    engineTypeChartData,
    categoryChartData,
    siteOccupancyData,
    subcontractorData,
    timelineData,
  };
}
